import { useTRPC } from '@/utils/trpc';
import {
  EXTERNAL_PRACTITIONER_TYPE_OPTIONS,
  EXTERNAL_SPECIALTY_OPTIONS,
  SNOMED_SYSTEM,
  US_STATES,
  isUsStateCode,
  type SnomedOption,
  type UsStateCode,
} from '@liza/fhir-utils';
import type { PatientAdminApiRouterOutputs } from '@liza/patient-admin-api';
import { Button } from '@liza/ui/components/ui/button';
import {
  Combobox,
  ComboboxCollection,
  ComboboxContent,
  ComboboxEmpty,
  ComboboxInput,
  ComboboxItem,
  ComboboxList,
} from '@liza/ui/components/ui/combobox';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '@liza/ui/components/ui/dialog';
import { Field, FieldError, FieldGroup, FieldLabel } from '@liza/ui/components/ui/field';
import { Input } from '@liza/ui/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@liza/ui/components/ui/select';
import { Spinner } from '@liza/ui/components/ui/spinner';
import type { Coding } from '@medplum/fhirtypes';
import { useForm } from '@tanstack/react-form';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { useDebouncedCallback } from 'use-debounce';
import { z } from 'zod';

type ExternalCareTeamMember = PatientAdminApiRouterOutputs['externalCareTeamMember']['listForOrg']['members'][number];
type ExternalPractice = PatientAdminApiRouterOutputs['externalCareTeamMember']['listPractices']['practices'][number];

const ExternalProviderFormSchema = z.object({
  firstName: z.string().min(1, 'First name is required'),
  lastName: z.string().min(1, 'Last name is required'),
  practiceLabel: z.string().min(1, 'Practice is required'),
  typeCode: z.string(),
  specialty: z.string(),
  phone: z.string(),
  fax: z.string(),
  email: z.email('Invalid email address').or(z.literal('')),
  addressLine1: z.string(),
  addressLine2: z.string(),
  city: z.string(),
  state: z.custom<UsStateCode>(isUsStateCode).or(z.literal('')),
  zipCode: z.string(),
});

type FormValues = z.infer<typeof ExternalProviderFormSchema>;

/**
 * The curated options, plus the member's stored coding when it isn't in the curated list —
 * so an edit of a member with an out-of-catalog code doesn't silently blank the field.
 */
const optionsWithStored = (options: readonly SnomedOption[], stored: Coding | undefined): SnomedOption[] => {
  if (!stored?.code || options.some((o) => o.code === stored.code)) return [...options];
  return [{ code: stored.code, display: stored.display ?? stored.code }, ...options];
};

/** Sentinel id for the synthetic "Create «name»" dropdown row; never a real practice id. */
const CREATE_PRACTICE_ID = '__create-practice__';

/** onInputValueChange reasons that reflect user edits — everything else is the primitive's own resets (e.g. snapping the input back on blur), which must not clobber typed free text. */
const USER_INPUT_REASONS = new Set(['input-change', 'input-clear', 'clear-press', 'item-press']);

const toCoding = (
  options: readonly SnomedOption[],
  code: string,
): { system: string; code: string; display?: string } | undefined => {
  if (!code) return undefined;
  const option = options.find((o) => o.code === code);
  return { system: SNOMED_SYSTEM, code, display: option?.display };
};

const toFormValues = (m: ExternalCareTeamMember | undefined): FormValues => {
  const empty = (s: string | undefined): string => s ?? '';
  const address = m?.address;
  return {
    firstName: empty(m?.firstName),
    lastName: empty(m?.lastName),
    practiceLabel: empty(m?.organizationName),
    typeCode: empty(m?.typeCode?.code),
    specialty: empty(m?.specialty?.code),
    phone: empty(m?.phone),
    fax: empty(m?.fax),
    email: empty(m?.email),
    addressLine1: empty(address?.line?.[0]),
    addressLine2: empty(address?.line?.[1]),
    city: empty(address?.city),
    state: isUsStateCode(address?.state) ? address.state : '',
    zipCode: empty(address?.postalCode),
  };
};

export type ExternalProviderFormDialogProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  organizationId: string;
  /** Called with the created member after a successful create (e.g. to link it in the same flow). */
  onCreated?: (member: ExternalCareTeamMember) => void;
} & ({ mode: 'create' } | { mode: 'edit'; member: ExternalCareTeamMember });

export const ExternalProviderFormDialog = (props: ExternalProviderFormDialogProps): React.ReactNode => {
  const { open, onOpenChange, organizationId, mode, onCreated } = props;
  const member = mode === 'edit' ? props.member : undefined;
  const { t } = useTranslation('common');
  const trpc = useTRPC();
  const queryClient = useQueryClient();

  // The practice field is a typeahead over the org's existing external practices: picking an
  // option reuses it (practiceId); free text creates a new one (practiceName) at submit time.
  const [selectedPractice, setSelectedPractice] = useState<ExternalPractice | null>(null);
  const [practiceTerm, setPracticeTerm] = useState('');
  const applyDebouncedPracticeTerm = useDebouncedCallback(setPracticeTerm, 300);

  const practicesQuery = useQuery(
    trpc.externalCareTeamMember.listPractices.queryOptions(
      { organizationId, term: practiceTerm.trim() || undefined },
      { enabled: open },
    ),
  );
  const practices = useMemo(() => practicesQuery.data?.practices ?? [], [practicesQuery.data]);

  const typeOptions = optionsWithStored(EXTERNAL_PRACTITIONER_TYPE_OPTIONS, member?.typeCode);
  const specialtyOptions = optionsWithStored(EXTERNAL_SPECIALTY_OPTIONS, member?.specialty);

  const invalidateAndClose = useCallback(() => {
    void queryClient.invalidateQueries({ queryKey: trpc.externalCareTeamMember.listForOrg.queryKey() });
    void queryClient.invalidateQueries({ queryKey: trpc.externalCareTeamMember.listPractices.queryKey() });
    onOpenChange(false);
  }, [queryClient, trpc, onOpenChange]);

  const createMutation = useMutation(
    trpc.externalCareTeamMember.create.mutationOptions({
      onSuccess: (data) => {
        toast.success(t('externalProviderCreated'));
        onCreated?.(data.member);
        invalidateAndClose();
      },
      onError: (error) => {
        toast.error(error.message);
      },
    }),
  );

  const updateMutation = useMutation(
    trpc.externalCareTeamMember.update.mutationOptions({
      onSuccess: () => {
        toast.success(t('externalProviderUpdated'));
        invalidateAndClose();
      },
      onError: (error) => {
        toast.error(error.message);
      },
    }),
  );

  /**
   * A picked option reuses that practice; typed free text is resolved against a fresh
   * practice fetch at submit time — the render-time list lags the debounced term, and a
   * stale miss would silently create a duplicate practice. In edit mode, resolving back
   * to the member's current practice omits both fields so the service keeps the existing link.
   */
  const resolvePractice = useCallback(
    async (label: string): Promise<{ practiceId?: string; practiceName?: string }> => {
      const trimmed = label.trim();
      const picked = selectedPractice && selectedPractice.name === trimmed ? selectedPractice : undefined;
      if (picked && picked.id !== CREATE_PRACTICE_ID) {
        return member && picked.id === member.organizationId ? {} : { practiceId: picked.id };
      }
      const { practices: freshPractices } = await queryClient.fetchQuery(
        trpc.externalCareTeamMember.listPractices.queryOptions({ organizationId, term: trimmed }),
      );
      const matched = freshPractices.find((p) => p.name.toLowerCase() === trimmed.toLowerCase());
      if (matched) {
        return member && matched.id === member.organizationId ? {} : { practiceId: matched.id };
      }
      if (member && trimmed.toLowerCase() === member.organizationName.toLowerCase()) return {};
      return { practiceName: trimmed };
    },
    [selectedPractice, member, queryClient, trpc, organizationId],
  );

  const buildPayload = useCallback(
    async (value: FormValues) => {
      const line = [value.addressLine1.trim(), value.addressLine2.trim()].filter(Boolean);
      const hasAddress = line.length > 0 || value.city || value.state || value.zipCode;
      return {
        firstName: value.firstName,
        lastName: value.lastName,
        ...(await resolvePractice(value.practiceLabel)),
        typeCode: toCoding(typeOptions, value.typeCode),
        specialty: toCoding(specialtyOptions, value.specialty),
        phone: value.phone || undefined,
        fax: value.fax || undefined,
        email: value.email || undefined,
        address: hasAddress
          ? {
              line: line.length > 0 ? line : undefined,
              city: value.city || undefined,
              state: value.state || undefined,
              postalCode: value.zipCode || undefined,
            }
          : undefined,
      };
    },
    [resolvePractice, typeOptions, specialtyOptions],
  );

  const form = useForm({
    defaultValues: toFormValues(member),
    validators: {
      onChange: ExternalProviderFormSchema,
      onSubmit: ExternalProviderFormSchema,
    },
    onSubmit: async ({ value }) => {
      try {
        const payload = await buildPayload(value);
        if (mode === 'create') {
          // organizationId attributes the member to this org (`meta.accounts`); updates
          // derive it from the existing role server-side, so only create sends it.
          createMutation.mutate({ ...payload, organizationId });
        } else if (member) {
          updateMutation.mutate({ ...payload, roleId: member.roleId });
        }
      } catch (error) {
        toast.error(error instanceof Error ? error.message : String(error));
      }
    },
  });

  useEffect(() => {
    if (open) {
      form.reset(toFormValues(member));
      setSelectedPractice(member ? { id: member.organizationId, name: member.organizationName } : null);
      setPracticeTerm('');
      applyDebouncedPracticeTerm.cancel();
    }
  }, [open, member, form, applyDebouncedPracticeTerm]);

  const isPending = createMutation.isPending || updateMutation.isPending;

  const snomedSelectField = (
    name: 'typeCode' | 'specialty',
    label: string,
    options: SnomedOption[],
    testId: string,
  ): React.ReactNode => (
    <form.Field
      name={name}
      // oxlint-disable-next-line react/no-children-prop
      children={(field) => (
        <Field>
          <FieldLabel htmlFor={field.name}>{label}</FieldLabel>
          <Select
            value={field.state.value ? field.state.value : '__none__'}
            onValueChange={(value) => field.handleChange(value === '__none__' ? '' : value)}
            disabled={isPending}
          >
            <SelectTrigger id={field.name} data-testid={testId} className="w-full" aria-label={label}>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="__none__">{t('none')}</SelectItem>
              {options.map((option) => (
                <SelectItem key={option.code} value={option.code}>
                  {option.display}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </Field>
      )}
    />
  );

  const textField = (name: keyof FormValues, label: string, type?: string, required = false): React.ReactNode => (
    <form.Field
      name={name}
      // oxlint-disable-next-line react/no-children-prop
      children={(field) => {
        const isInvalid = field.state.meta.isTouched && !field.state.meta.isValid;
        return (
          <Field data-invalid={isInvalid}>
            <FieldLabel htmlFor={field.name}>
              {label}
              {required && <span className="text-destructive"> *</span>}
            </FieldLabel>
            <Input
              id={field.name}
              type={type}
              value={field.state.value}
              onBlur={field.handleBlur}
              onChange={(e) => field.handleChange(e.target.value)}
              aria-invalid={isInvalid}
              aria-required={required || undefined}
              disabled={isPending}
            />
            {isInvalid && <FieldError errors={field.state.meta.errors} />}
          </Field>
        );
      }}
    />
  );

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{mode === 'create' ? t('addExternalProvider') : t('editExternalProvider')}</DialogTitle>
        </DialogHeader>
        <form
          id="external-provider-form"
          onSubmit={(e) => {
            e.preventDefault();
            void form.handleSubmit();
          }}
          className="flex flex-col gap-4"
        >
          <FieldGroup>
            {textField('firstName', t('firstName'), undefined, true)}
            {textField('lastName', t('lastName'), undefined, true)}
            <form.Field
              name="practiceLabel"
              // oxlint-disable-next-line react/no-children-prop
              children={(field) => {
                const isInvalid = field.state.meta.isTouched && !field.state.meta.isValid;
                const trimmedInput = field.state.value.trim();
                const hasExactMatch = practices.some((p) => p.name.toLowerCase() === trimmedInput.toLowerCase());
                const comboItems =
                  trimmedInput && !hasExactMatch
                    ? [...practices, { id: CREATE_PRACTICE_ID, name: trimmedInput }]
                    : practices;
                return (
                  <Field data-invalid={isInvalid}>
                    <FieldLabel htmlFor={field.name}>
                      {t('practice')}
                      <span className="text-destructive"> *</span>
                    </FieldLabel>
                    <Combobox<ExternalPractice>
                      items={comboItems}
                      value={selectedPractice}
                      onValueChange={(practice) => {
                        setSelectedPractice(practice ?? null);
                        if (practice) field.handleChange(practice.name);
                      }}
                      inputValue={field.state.value}
                      onInputValueChange={(value, details) => {
                        if (!USER_INPUT_REASONS.has(details.reason)) return;
                        field.handleChange(value);
                        applyDebouncedPracticeTerm(value);
                        if (selectedPractice && value !== selectedPractice.name) setSelectedPractice(null);
                      }}
                      itemToStringLabel={(practice) => practice.name}
                      itemToStringValue={(practice) => practice.id}
                      isItemEqualToValue={(a, b) => a.id === b.id}
                      filter={() => true}
                    >
                      <ComboboxInput
                        id={field.name}
                        data-testid="external-provider-practice"
                        aria-label={t('practice')}
                        aria-required
                        aria-invalid={isInvalid}
                        onBlur={field.handleBlur}
                        disabled={isPending}
                      />
                      <ComboboxContent className="pointer-events-auto" onWheel={(e) => e.stopPropagation()}>
                        <ComboboxList>
                          <ComboboxEmpty>
                            {practicesQuery.isLoading ? <Spinner className="size-4" /> : t('noPracticesFound')}
                          </ComboboxEmpty>
                          <ComboboxCollection>
                            {(practice) => (
                              <ComboboxItem key={practice.id} value={practice}>
                                {practice.id === CREATE_PRACTICE_ID
                                  ? t('createPracticeOption', { name: practice.name })
                                  : practice.name}
                              </ComboboxItem>
                            )}
                          </ComboboxCollection>
                        </ComboboxList>
                      </ComboboxContent>
                    </Combobox>
                    <p className="text-xs text-muted-foreground">{t('practiceHelper')}</p>
                    {isInvalid && <FieldError errors={field.state.meta.errors} />}
                  </Field>
                );
              }}
            />
            {snomedSelectField('typeCode', t('practitionerType'), typeOptions, 'external-provider-type')}
            {snomedSelectField('specialty', t('specialty'), specialtyOptions, 'external-provider-specialty')}
            {textField('phone', t('phoneNumber'), 'tel')}
            {textField('fax', t('faxNumber'), 'tel')}
            {textField('email', t('emailAddress'), 'email')}
            {textField('addressLine1', t('addressLine1'))}
            {textField('addressLine2', t('addressLine2'))}
            {textField('city', t('city'))}
            <form.Field
              name="state"
              // oxlint-disable-next-line react/no-children-prop
              children={(field) => (
                <Field>
                  <FieldLabel htmlFor={field.name}>{t('state')}</FieldLabel>
                  <Select
                    value={field.state.value ? field.state.value : '__none__'}
                    onValueChange={(value) => field.handleChange(isUsStateCode(value) ? value : '')}
                    disabled={isPending}
                  >
                    <SelectTrigger id={field.name} className="w-full" aria-label={t('state')}>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="__none__">{t('none')}</SelectItem>
                      {US_STATES.map((usState) => (
                        <SelectItem key={usState.code} value={usState.code}>
                          {usState.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </Field>
              )}
            />
            {textField('zipCode', t('zipCode'))}
          </FieldGroup>
        </form>
        <DialogFooter>
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            type="button"
            data-testid="cancel-external-provider-button"
          >
            {t('cancel')}
          </Button>
          <form.Subscribe
            selector={(state) => [state.canSubmit, state.isSubmitting]}
            // oxlint-disable-next-line react/no-children-prop
            children={([canSubmit]) => (
              <Button
                type="submit"
                form="external-provider-form"
                disabled={!canSubmit || isPending}
                data-testid="submit-external-provider-button"
              >
                {isPending ? <Spinner className="mr-2 size-4" /> : null}
                {mode === 'create' ? t('create') : t('save')}
              </Button>
            )}
          />
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
